import { useId, useState } from 'react';

/**
 * Chart primitives for the $mallX console.
 *
 * Inline SVG rather than a charting library: these are small, the bundle is
 * already shipped to customers on a phone, and a dependency would cost more
 * than the ~200 lines here.
 *
 * COLOUR — the palette below is validated, not chosen by eye. Run against the
 * real card surface (#0a0a12) it passes the lightness band, chroma floor,
 * colour-vision separation (worst adjacent ΔE 8.4 protan), the normal-vision
 * floor and 3:1 contrast. Do not add a ninth hue: fold extra series into
 * "Other" or facet instead of generating one, which is what breaks a palette.
 *
 * Profit and loss is POLARITY, not identity, so it uses the brand's own
 * green/red rather than a categorical slot — the same semantics as every other
 * number in the product.
 */
export const SERIES = [
  '#3987e5', '#d95926', '#199e70', '#c98500',
  '#d55181', '#008300', '#9085e9', '#e66767',
] as const;

const UP = '#2dd05f';
const DOWN = '#ed3838';
const GRID = '#24242f';
const INK_DIM = '#74748a';

const fmtMoney = (n: number): string => `${n < 0 ? '-' : ''}$${Math.abs(n).toFixed(2)}`;
const shortDate = (iso: string): string => iso.slice(5).replace('-', '/');

/**
 * Cumulative profit over time. One series, so no legend box — the title names
 * it. Area fill is tinted by the END value's sign, so the shape reads as
 * "ahead" or "behind" before any number is read.
 */
export function EquityCurve({
  points,
  height = 168,
}: {
  points: Array<{ date: string; net: number }>;
  height?: number;
}) {
  const gid = useId();
  const [hover, setHover] = useState<number | null>(null);
  if (points.length < 2) return <p className="muted">—</p>;

  const W = 320;
  const H = height;
  const PAD = { t: 10, r: 8, b: 18, l: 8 };
  const vals = points.map((p) => p.net);
  const min = Math.min(0, ...vals);
  const max = Math.max(0, ...vals);
  const span = max - min || 1;
  const x = (i: number) => PAD.l + (i / (points.length - 1)) * (W - PAD.l - PAD.r);
  const y = (v: number) => PAD.t + (1 - (v - min) / span) * (H - PAD.t - PAD.b);

  const line = points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.net).toFixed(1)}`).join(' ');
  const area = `${line} L${x(points.length - 1).toFixed(1)},${y(min)} L${x(0).toFixed(1)},${y(min)} Z`;
  const end = points[points.length - 1].net;
  const stroke = end >= 0 ? UP : DOWN;
  const zeroY = y(0);
  const active = hover ?? points.length - 1;

  return (
    <div className="viz-wrap">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="viz-svg"
        role="img"
        aria-label={`Cumulative profit, ending ${fmtMoney(end)}`}
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const r = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
          const rel = ((e.clientX - r.left) / r.width) * W;
          const i = Math.round(((rel - PAD.l) / (W - PAD.l - PAD.r)) * (points.length - 1));
          setHover(Math.max(0, Math.min(points.length - 1, i)));
        }}
      >
        <defs>
          <linearGradient id={`g${gid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={stroke} stopOpacity="0.28" />
            <stop offset="100%" stopColor={stroke} stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* Zero line is the reference an operator reads against, so it stays
            visible while the rest of the grid recedes. */}
        <line x1={PAD.l} x2={W - PAD.r} y1={zeroY} y2={zeroY} stroke={GRID} strokeWidth="1" />
        <path d={area} fill={`url(#g${gid})`} />
        <path d={line} fill="none" stroke={stroke} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {/* Crosshair + marker: an SVG chart is interactive by default. */}
        <line x1={x(active)} x2={x(active)} y1={PAD.t} y2={H - PAD.b} stroke={GRID} strokeWidth="1" />
        <circle cx={x(active)} cy={y(points[active].net)} r="4" fill={stroke} stroke="#0a0a12" strokeWidth="2" />
        <text x={PAD.l} y={H - 5} fontSize="9" fill={INK_DIM}>{shortDate(points[0].date)}</text>
        <text x={W - PAD.r} y={H - 5} fontSize="9" fill={INK_DIM} textAnchor="end">
          {shortDate(points[points.length - 1].date)}
        </text>
      </svg>
      <div className="viz-read">
        <span className="muted">{shortDate(points[active].date)}</span>
        <strong className={points[active].net >= 0 ? 'up' : 'down'}>{fmtMoney(points[active].net)}</strong>
      </div>
    </div>
  );
}

/**
 * Daily profit and loss. Bars diverge from a zero baseline because the sign is
 * the point; colour is polarity, not identity, so no legend is needed.
 * 2px gaps between bars per the spacer rule.
 */
export function DailyPnlBars({ points, height = 132 }: { points: Array<{ date: string; net: number; trades: number }>; height?: number }) {
  const [hover, setHover] = useState<number | null>(null);
  if (!points.length) return <p className="muted">—</p>;

  const W = 320;
  const H = height;
  const PAD = { t: 8, b: 18 };
  const mag = Math.max(...points.map((p) => Math.abs(p.net)), 1);
  const bw = (W / points.length) - 2;
  const zero = PAD.t + (H - PAD.t - PAD.b) / 2;
  const scale = (H - PAD.t - PAD.b) / 2 / mag;

  return (
    <div className="viz-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} className="viz-svg" role="img" aria-label="Daily profit and loss" onMouseLeave={() => setHover(null)}>
        <line x1="0" x2={W} y1={zero} y2={zero} stroke={GRID} strokeWidth="1" />
        {points.map((p, i) => {
          const h = Math.max(Math.abs(p.net) * scale, p.net === 0 ? 0 : 2);
          const up = p.net >= 0;
          return (
            <rect
              key={p.date}
              x={i * (bw + 2) + 1}
              y={up ? zero - h : zero}
              width={bw}
              height={h}
              rx="2"
              fill={up ? UP : DOWN}
              opacity={hover === null || hover === i ? 1 : 0.45}
              onMouseEnter={() => setHover(i)}
            />
          );
        })}
      </svg>
      <div className="viz-read">
        {hover === null ? (
          <span className="muted">{points.length} days</span>
        ) : (
          <>
            <span className="muted">{shortDate(points[hover].date)} · {points[hover].trades} trades</span>
            <strong className={points[hover].net >= 0 ? 'up' : 'down'}>{fmtMoney(points[hover].net)}</strong>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Win/loss split as a single proportional bar. A 2px surface gap separates the
 * two fills so they never read as one block, and the counts are labelled
 * directly rather than relying on colour alone.
 */
export function WinLossBar({ wins, losses }: { wins: number; losses: number }) {
  const total = wins + losses;
  if (!total) return <p className="muted">—</p>;
  const pct = Math.round((wins / total) * 100);
  return (
    <div className="wl">
      <div className="wl-bar" role="img" aria-label={`${wins} wins, ${losses} losses, ${pct} percent win rate`}>
        <span style={{ width: `${(wins / total) * 100}%`, background: UP }} />
        <span style={{ width: `${(losses / total) * 100}%`, background: DOWN }} />
      </div>
      <div className="wl-legend">
        <span><i style={{ background: UP }} />{wins} W</span>
        <span><i style={{ background: DOWN }} />{losses} L</span>
        <strong>{pct}%</strong>
      </div>
    </div>
  );
}

/**
 * Per-customer comparison. Horizontal bars because the labels are email
 * addresses, which do not fit under vertical ones. Colour is polarity again.
 */
export function CustomerBars({ rows }: { rows: Array<{ label: string; value: number }> }) {
  if (!rows.length) return <p className="muted">—</p>;
  const mag = Math.max(...rows.map((r) => Math.abs(r.value)), 1);
  return (
    <ul className="hbars">
      {rows.map((r) => (
        <li key={r.label}>
          <span className="hbar-label" title={r.label}>{r.label}</span>
          <span className="hbar-track">
            <span
              className="hbar-fill"
              style={{ width: `${(Math.abs(r.value) / mag) * 100}%`, background: r.value >= 0 ? UP : DOWN }}
            />
          </span>
          <span className={`hbar-val ${r.value >= 0 ? 'up' : 'down'}`}>{fmtMoney(r.value)}</span>
        </li>
      ))}
    </ul>
  );
}
